import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { THUMBNAIL_LAYOUT } from "./thumbnailLayout";
import { OPEN_SANS_300_BASE64, OPEN_SANS_400_BASE64 } from "./fontEmbedding";

/**
 * ThumbnailSvgRenderer - NEW SVG-based thumbnail renderer
 * 
 * Purpose: Render the 160×160 thumbnail as a native SVG React component.
 * This renderer can be used both for the preview and for saving, ensuring
 * the preview and exported SVG are identical.
 * 
 * Props:
 *   - state: { selected, year, details: { creatorSpeaker, title, topic, sku, naCrNumber, anzCrNumber } }
 *   - preCalculatedText: { creatorLines, titleLines, topicLines, skuLines, crLines }
 *   - previewBackground: CSS background string or hex color
 *   - useDarkPreviewText: boolean for text color (light = false, dark = true)
 *   - textColor: explicit text color override (optional)
 */
export const ThumbnailSvgRenderer = React.forwardRef(function ThumbnailSvgRenderer(
  {
    state,
    preCalculatedText,
    previewBackground,
    useDarkPreviewText,
    textColor: explicitTextColor,
  },
  ref
) {
  const svgRef = React.useRef(null);

  // Expose getSvgString() via ref for saving/serialization
  React.useImperativeHandle(ref, () => ({
    getSvgString: () => {
      if (!svgRef.current) return null;
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgRef.current);
      
      // DEBUG: Log font-weight values in the serialized SVG
      const fontWeightMatches = svgString.match(/font-weight="[^"]*"/g);
      console.log("DEBUG: Font-weight values in SVG:", fontWeightMatches);
      
      return svgString;
    },
    // IMPORTANT: Expose the SVG element so it can be rendered to canvas with font access
    getSvgElement: () => svgRef.current,
  }));

  // Compute text color based on background luminance or explicit override
  const textColor = useMemo(() => {
    if (explicitTextColor) return explicitTextColor;
    return useDarkPreviewText ? "#243746" : "#ffffff";
  }, [useDarkPreviewText, explicitTextColor]);

  // Parse CSS gradient or use solid color
  const backgroundDef = useMemo(() => {
    if (!previewBackground) return null;

    if (previewBackground.startsWith("linear-gradient")) {
      // Parse: linear-gradient(40deg, #FFFFFF, #243746)
      const gradientMatch = previewBackground.match(/linear-gradient\((\d+)deg,\s*(.+?)\)/);
      if (gradientMatch) {
        const angle = Number(gradientMatch[1]) || 40;
        const colorString = gradientMatch[2];
        const gradientColors = colorString
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c && c.startsWith("#"))
          .slice(0, 3);

        // Convert CSS angle to SVG gradient endpoints
        const radians = (angle * Math.PI) / 180;
        const dx = Math.sin(radians);
        const dy = -Math.cos(radians);
        const x1 = 50 - dx * 50;
        const y1 = 50 - dy * 50;
        const x2 = 50 + dx * 50;
        const y2 = 50 + dy * 50;

        const stops = gradientColors
          .map((color, idx) => {
            const offset = (idx / (gradientColors.length - 1)) * 100;
            return `<stop offset="${offset}%" stop-color="${color}" />`;
          })
          .join("");

        return (
          <defs>
            <linearGradient
              id="thumbnailGradient"
              x1={`${x1}%`}
              y1={`${y1}%`}
              x2={`${x2}%`}
              y2={`${y2}%`}
            >
              {gradientColors.map((color, idx) => (
                <stop
                  key={idx}
                  offset={`${(idx / (gradientColors.length - 1)) * 100}%`}
                  stopColor={color}
                />
              ))}
            </linearGradient>
          </defs>
        );
      }
    }
    return null;
  }, [previewBackground]);

  // Get fill for background rectangle
  const getBgFill = () => {
    if (!previewBackground) return "#ffffff";
    if (previewBackground.startsWith("linear-gradient")) return "url(#thumbnailGradient)";
    return previewBackground;
  };

  // Note: React automatically escapes text content, so manual escaping is not needed

  // Helper to split SKU into base and version parts
  // e.g., "WW1339v2.24" -> { base: "WW1339", version: "v2.24" }
  const parseSku = (sku) => {
    const match = String(sku || "").match(/^([^\d]*\d+)([a-zA-Z]v[\d.]+)$/);
    if (match) {
      return { base: match[1], version: match[2] };
    }
    return { base: sku, version: null };
  };

  // Get badge data
  const code = state?.selected || "";
  const year = state?.year || "";

  // Safe access to text arrays
  const creatorLines = preCalculatedText?.creatorLines || [""];
  const titleLines = preCalculatedText?.titleLines || [""];
  const topicLines = preCalculatedText?.topicLines || [];
  const skuLines = preCalculatedText?.skuLines || [""];
  const crLines = preCalculatedText?.crLines || [];

  // Y positioning calculations
  const calculateTextPositioning = () => {
    const layout = THUMBNAIL_LAYOUT;
    let currentY = layout.textContainer.top;
    const sectionGap = layout.textContainer.gap || 1;

    // Creator block
    const creatorHeight = creatorLines.length * layout.primary.lineHeight;
    const creatorY = currentY + layout.primary.lineHeight / 2;
    currentY += creatorHeight + sectionGap;

    // Title block
    const titleHeight = titleLines.length * layout.title.lineHeight;
    const titleY = currentY + layout.title.lineHeight / 2;
    currentY += titleHeight + sectionGap;

    // Topic block
    const topicHeight = topicLines.length * layout.secondary.lineHeight;
    const topicY = currentY + layout.secondary.lineHeight / 2;

    // Meta positioning (moved up slightly for better spacing)
    const metaBottomOffset = 16; // Adjusted for proper bottom padding around SKU/CR#
    const skuY = layout.height - metaBottomOffset - 2 - layout.sku.lineHeight / 2;
    // CR# positioned below SKU: (SKU center to bottom) + gap + (CR# center)
    const crY = skuY + (layout.sku.lineHeight / 2) + (layout.meta.gap || 1) + (layout.cr.lineHeight / 2);

    return {
      creatorY,
      titleY,
      topicY,
      skuY,
      crY,
    };
  };

  const positions = useMemo(() => calculateTextPositioning(), [creatorLines, titleLines, topicLines]);

  // Badge positioning and centering
  const badgeX = THUMBNAIL_LAYOUT.width - THUMBNAIL_LAYOUT.badge.right - THUMBNAIL_LAYOUT.badge.width / 2;
  const badgeBoxTop = THUMBNAIL_LAYOUT.badge.top;
  const badgeBoxLeft = THUMBNAIL_LAYOUT.width - THUMBNAIL_LAYOUT.badge.right - THUMBNAIL_LAYOUT.badge.width;
  const badgeContainerHeight = THUMBNAIL_LAYOUT.badge.height;
  const badgeCodeHeight = THUMBNAIL_LAYOUT.badge.code.lineHeight;
  const badgeYearHeight = THUMBNAIL_LAYOUT.badge.year.lineHeight;

  // Better centering: center the badge content vertically within the badge box
  const totalTextHeight = badgeCodeHeight + badgeYearHeight;
  const badgeBoxCenterY = badgeBoxTop + badgeContainerHeight / 2;
  const badgeStartY = badgeBoxCenterY - totalTextHeight / 2 + 1;
  const codeTextY = badgeStartY + badgeCodeHeight / 2;
  const yearTextY = badgeStartY + badgeCodeHeight + badgeYearHeight / 2;

  // Reduce font size for FMA badge to fit better in the box
  const codeFontSize = code === "FMA" ? 14 : THUMBNAIL_LAYOUT.badge.code.fontSize;

  // Render a single text element with given properties
  const renderTextElement = (text, x, y, fontSize, fontWeight, lineHeight, opacity = 1, letterSpacing = 0) => {
    const key = `${text}-${x}-${y}`;
    return (
      <text
        key={key}
        x={x}
        y={y}
        fontFamily={THUMBNAIL_LAYOUT.primary.fontFamily}
        fontSize={fontSize}
        fontWeight={String(fontWeight)}
        fill={textColor}
        opacity={opacity}
        dominantBaseline="middle"
        textAnchor="middle"
        letterSpacing={letterSpacing === 0 ? undefined : letterSpacing}
      >
        {text}
      </text>
    );
  };

  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      width={THUMBNAIL_LAYOUT.width}
      height={THUMBNAIL_LAYOUT.height}
      viewBox={`0 0 ${THUMBNAIL_LAYOUT.width} ${THUMBNAIL_LAYOUT.height}`}
      style={{ borderRadius: "15px" }}
    >
      {/* Definitions for gradients and styles */}
      {backgroundDef}
      <defs>
        <style type="text/css">
          {`
            @font-face {
              font-family: 'Open Sans';
              font-weight: 300;
              src: url(${OPEN_SANS_300_BASE64}) format('truetype');
            }
            @font-face {
              font-family: 'Open Sans';
              font-weight: 400;
              src: url(${OPEN_SANS_400_BASE64}) format('truetype');
            }
            text { 
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
          `}
        </style>
      </defs>

      {/* Background */}
      <rect
        x="0"
        y="0"
        width={THUMBNAIL_LAYOUT.width}
        height={THUMBNAIL_LAYOUT.height}
        fill={getBgFill()}
        rx={THUMBNAIL_LAYOUT.borderRadius}
        ry={THUMBNAIL_LAYOUT.borderRadius}
      />

      {/* Badge border */}
      <rect
        x={THUMBNAIL_LAYOUT.width - THUMBNAIL_LAYOUT.badge.right - THUMBNAIL_LAYOUT.badge.width}
        y={THUMBNAIL_LAYOUT.badge.top}
        width={THUMBNAIL_LAYOUT.badge.width}
        height={THUMBNAIL_LAYOUT.badge.height}
        fill="none"
        stroke={textColor === "#ffffff" ? "rgba(255,255,255,0.7)" : "rgba(36,55,70,0.65)"}
        strokeWidth={THUMBNAIL_LAYOUT.badge.borderWidth}
        rx="0"
      />

      {/* Badge code text */}
      {renderTextElement(
        code,
        badgeX,
        codeTextY,
        codeFontSize,
        THUMBNAIL_LAYOUT.badge.code.fontWeight
      )}

      {/* Badge year text */}
      {year &&
        renderTextElement(
          year,
          badgeX,
          yearTextY,
          THUMBNAIL_LAYOUT.badge.year.fontSize,
          THUMBNAIL_LAYOUT.badge.year.fontWeight,
          THUMBNAIL_LAYOUT.badge.year.lineHeight,
          THUMBNAIL_LAYOUT.badge.year.opacity,
          THUMBNAIL_LAYOUT.badge.year.letterSpacing
        )}

      {/* Creator/Primary text */}
      {creatorLines.map((line, idx) => (
        <text
          key={`creator-${idx}`}
          x={THUMBNAIL_LAYOUT.textContainer.left}
          y={positions.creatorY + idx * THUMBNAIL_LAYOUT.primary.lineHeight}
          fontFamily={THUMBNAIL_LAYOUT.primary.fontFamily}
          fontSize={THUMBNAIL_LAYOUT.primary.fontSize}
          fontWeight={String(THUMBNAIL_LAYOUT.primary.fontWeight)}
          fill={textColor}
          dominantBaseline="middle"
          textAnchor="start"
        >
          {line}
        </text>
      ))}

      {/* Title text */}
      {titleLines.map((line, idx) => (
        <text
          key={`title-${idx}`}
          x={THUMBNAIL_LAYOUT.textContainer.left}
          y={positions.titleY + idx * THUMBNAIL_LAYOUT.title.lineHeight}
          fontFamily={THUMBNAIL_LAYOUT.title.fontFamily}
          fontSize={THUMBNAIL_LAYOUT.title.fontSize}
          fontWeight={String(THUMBNAIL_LAYOUT.title.fontWeight)}
          fill={textColor}
          opacity={THUMBNAIL_LAYOUT.title.opacity}
          dominantBaseline="middle"
        >
          {line}
        </text>
      ))}

      {/* Topic/Secondary text */}
      {topicLines.map((line, idx) => (
        <text
          key={`topic-${idx}`}
          x={THUMBNAIL_LAYOUT.textContainer.left}
          y={positions.topicY + idx * THUMBNAIL_LAYOUT.secondary.lineHeight}
          fontFamily={THUMBNAIL_LAYOUT.secondary.fontFamily}
          fontSize={THUMBNAIL_LAYOUT.secondary.fontSize}
          fontWeight={String(THUMBNAIL_LAYOUT.secondary.fontWeight)}
          fill={textColor}
          opacity={THUMBNAIL_LAYOUT.secondary.opacity}
          dominantBaseline="middle"
          letterSpacing={THUMBNAIL_LAYOUT.secondary.letterSpacing}
        >
          {line}
        </text>
      ))}

      {/* SKU text with split font weights */}
      {(() => {
        const skuText = skuLines[0] || "";
        const { base, version } = parseSku(skuText);
        return (
          <text
            x={THUMBNAIL_LAYOUT.meta.left}
            y={positions.skuY}
            fontFamily={THUMBNAIL_LAYOUT.sku.fontFamily}
            fontSize={THUMBNAIL_LAYOUT.sku.fontSize}
            fontWeight={String(THUMBNAIL_LAYOUT.sku.fontWeight)}
            fill={textColor}
            dominantBaseline="middle"
            textAnchor="start"
          >
            {base}
            {version && (
              <tspan
                fontSize={THUMBNAIL_LAYOUT.skuVersion.fontSize}
                fontWeight={String(THUMBNAIL_LAYOUT.skuVersion.fontWeight)}
              >
                {version}
              </tspan>
            )}
          </text>
        );
      })()}

      {/* CR text */}
      {crLines[0] && (
        <text
          x={THUMBNAIL_LAYOUT.meta.left}
          y={positions.crY}
          fontFamily={THUMBNAIL_LAYOUT.cr.fontFamily}
          fontSize={THUMBNAIL_LAYOUT.cr.fontSize}
          fontWeight={String(THUMBNAIL_LAYOUT.cr.fontWeight)}
          fill={textColor}
          opacity={THUMBNAIL_LAYOUT.cr.opacity}
          dominantBaseline="middle"
          letterSpacing={THUMBNAIL_LAYOUT.cr.letterSpacing}
        >
          {`CR# ${crLines[0]}`}
        </text>
      )}
    </svg>
  );
});

ThumbnailSvgRenderer.propTypes = {
  state: PropTypes.shape({
    selected: PropTypes.string,
    year: PropTypes.string,
    details: PropTypes.shape({
      creatorSpeaker: PropTypes.string,
      title: PropTypes.string,
      topic: PropTypes.string,
      sku: PropTypes.string,
      naCrNumber: PropTypes.string,
      anzCrNumber: PropTypes.string,
    }),
  }),
  preCalculatedText: PropTypes.shape({
    creatorLines: PropTypes.arrayOf(PropTypes.string),
    titleLines: PropTypes.arrayOf(PropTypes.string),
    topicLines: PropTypes.arrayOf(PropTypes.string),
    skuLines: PropTypes.arrayOf(PropTypes.string),
    crLines: PropTypes.arrayOf(PropTypes.string),
  }),
  previewBackground: PropTypes.string,
  useDarkPreviewText: PropTypes.bool,
  textColor: PropTypes.string,
};

ThumbnailSvgRenderer.defaultProps = {
  state: {},
  preCalculatedText: {
    creatorLines: [""],
    titleLines: [""],
    topicLines: [],
    skuLines: [""],
    crLines: [],
  },
  previewBackground: "#243746",
  useDarkPreviewText: false,
  textColor: null,
};

export default ThumbnailSvgRenderer;
